import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useSettings } from "../../context/SettingsContext";

// Picks a clean y-axis ceiling + tick step that hugs the data closely.
// Chooses the smallest "nice" step (1, 2, 2.5, 5 × 10^k) whose ceiling keeps the
// tick count at/under `maxTicks`, so a max of 32 lands on 35 (step 5) rather than
// overshooting to 50.
function niceScale(maxValue, maxTicks = 6) {
  if (!maxValue || maxValue <= 0) return { max: 4, step: 1 };
  const candidates = [];
  for (let p = -2; p <= 4; p++) {
    const mag = Math.pow(10, p);
    [1, 2, 2.5, 5].forEach((m) => candidates.push(m * mag));
  }
  candidates.sort((a, b) => a - b);
  for (const step of candidates) {
    const max = Math.ceil(maxValue / step - 1e-9) * step;
    if (max / step <= maxTicks) return { max, step };
  }
  const step = candidates[candidates.length - 1];
  return { max: Math.ceil(maxValue / step) * step, step };
}

const VoteBarChart = ({ counts, labels, onThresholdChange, hideSlider = false, externalThreshold = null, fillHeight = false }) => {
  const { getEnabledKeys } = useSettings();
  const enabledCount = getEnabledKeys().length || 1;

  // Handle empty data gracefully
  const hasData = counts.length > 0;
  const maxCount = hasData ? Math.max(...counts) : 10;
  const minCount = hasData ? Math.min(...counts) : 0;

  // Calculate average for default threshold
  const avgCount = hasData ? counts.reduce((sum, count) => sum + count, 0) / counts.length : 5;

  // Set default threshold to nearest .5 (always ending in .5)
  const defaultThreshold = Math.floor(avgCount) + 0.5;

  // Set min and max for slider to nearest .5
  // Minimum should be 0.5, not negative
  const sliderMin = Math.max(0.5, Math.floor(minCount - 0.5) + 0.5);
  // Maximum should not exceed the actual max count
  const sliderMax = Math.floor(maxCount) + 0.5;

  const [sliderValue, setSliderValue] = useState(defaultThreshold);

  // Use external threshold if provided, otherwise use internal slider value
  const currentThreshold = externalThreshold !== null ? externalThreshold : sliderValue;

  // Notify parent component when threshold changes (only for internal slider)
  useEffect(() => {
    if (onThresholdChange && !hideSlider) {
      onThresholdChange(sliderValue);
    }
  }, [sliderValue, onThresholdChange, hideSlider]);

  // Measure the container so the SVG always spans the available width.
  const wrapRef = useRef(null);
  const [measuredW, setMeasuredW] = useState(560);
  const [hover, setHover] = useState(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setMeasuredW(Math.max(260, el.clientWidth));
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, []);

  const geom = useMemo(() => {
    const padL = 34;
    const padR = 12;
    const padT = 16;
    const padB = 26;
    const n = counts.length;

    const svgW = measuredW;
    const svgH = fillHeight ? 300 : 240;
    const innerW = svgW - padL - padR;
    const innerH = svgH - padT - padB;
    const slot = n > 0 ? innerW / n : innerW;
    const barW = Math.min(slot * 0.62, 44);

    const maxVal = Math.max(1, ...counts.map((c) => Number(c) || 0), currentThreshold || 0);
    const { max: yMax, step } = niceScale(maxVal);

    const yFor = (v) => padT + innerH - (Math.max(0, v) / yMax) * innerH;
    const xFor = (i) => padL + slot * i + slot / 2;

    const ticks = [];
    const tickCount = Math.round(yMax / step);
    for (let t = 0; t <= tickCount; t++) {
      const v = t * step;
      ticks.push({ v, y: yFor(v) });
    }

    return { padL, padR, padT, padB, svgW, svgH, slot, barW, yMax, yFor, xFor, ticks };
  }, [counts, measuredW, currentThreshold, fillHeight]);

  const { padL, padR, svgW, svgH, slot, barW, yFor, xFor, ticks } = geom;
  const thresholdY = yFor(currentThreshold);
  const baselineY = yFor(0);
  const showThresholdLine = currentThreshold <= geom.yMax;

  // Shorten Betfair to BF when the chart is narrow (mobile widths)
  const getDisplayLabel = (label) => (label === "Betfair" && measuredW < 480 ? "BF" : label);

  // Changing this remounts the bars so the grow-in animation replays — on first
  // load and whenever the dataset selection changes.
  const animKey = `${counts.length}|${counts.join(',')}`;

  return (
    <div className={`rounded-xl border border-slate-800 bg-slate-950/60 p-3 sm:p-4 w-full ${fillHeight ? 'h-full flex flex-col' : ''}`}>
      <style>{`
        @keyframes voteBarGrow { from { transform: scaleY(0.001); } to { transform: scaleY(1); } }
        .vote-bar-grow { transform-box: fill-box; transform-origin: 50% 100%; animation: voteBarGrow 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        .vote-threshold-slider::-webkit-slider-thumb {
          appearance: none;
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #0f172a;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
        }
        .vote-threshold-slider::-moz-range-thumb {
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #0f172a;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
        }
      `}</style>

      {/* Chart */}
      <div ref={wrapRef} className={`relative w-full ${fillHeight ? 'flex-1' : ''}`}>
        {hasData ? (
          <svg width={svgW} height={svgH} className="block" role="img" aria-label="Projected votes by dataset">
            <defs>
              <linearGradient id="voteBarHit" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" />
                <stop offset="100%" stopColor="#059669" />
              </linearGradient>
              <linearGradient id="voteBarMiss" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fb7185" />
                <stop offset="100%" stopColor="#e11d48" />
              </linearGradient>
            </defs>

            {/* Gridlines + y labels */}
            {ticks.map((t, i) => (
              <g key={i}>
                <line x1={padL} y1={t.y} x2={svgW - padR} y2={t.y} stroke="#1e293b" strokeWidth="1" />
                <text x={padL - 6} y={t.y + 3} textAnchor="end" className="fill-slate-500" style={{ fontSize: 9 }}>
                  {Number.isInteger(t.v) ? t.v : t.v.toFixed(1)}
                </text>
              </g>
            ))}

            {/* Threshold line (over/under) */}
            {showThresholdLine && (
              <g>
                <line x1={padL} y1={thresholdY} x2={svgW - padR} y2={thresholdY} stroke="#e2e8f0" strokeWidth="1.25" strokeDasharray="2 3" opacity="0.7" />
                <text x={padL + 2} y={thresholdY - 4} className="fill-slate-300" style={{ fontSize: 9 }}>
                  line {currentThreshold}
                </text>
              </g>
            )}

            {/* Bars (keyed group so the grow-in animation replays on change) */}
            <g key={animKey}>
              {counts.map((count, index) => {
                const v = Number(count) || 0;
                const x = xFor(index);
                const y = yFor(v);
                const h = Math.max(0, baselineY - y);
                const isAboveLine = count > currentThreshold;
                const dim = hover != null && hover !== index;
                const growDelay = `${Math.min(index * 40, 500)}ms`;
                const inside = h >= 16;
                return (
                  <g
                    key={index}
                    onMouseEnter={() => setHover(index)}
                    onMouseLeave={() => setHover((h2) => (h2 === index ? null : h2))}
                    style={{ cursor: 'default' }}
                  >
                    {/* Hover hit area */}
                    <rect x={x - slot / 2} y={geom.padT} width={slot} height={svgH - geom.padT - geom.padB} fill="transparent" />
                    <rect
                      x={x - barW / 2}
                      y={y}
                      width={barW}
                      height={h}
                      rx={3}
                      fill={isAboveLine ? 'url(#voteBarHit)' : 'url(#voteBarMiss)'}
                      opacity={dim ? 0.45 : 1}
                      className="vote-bar-grow"
                      style={{ transition: 'opacity 0.12s, fill 0.3s', animationDelay: growDelay }}
                    />
                    {/* Value label — inside the bar when tall enough, otherwise above it */}
                    <text
                      x={x}
                      y={inside ? y + 12 : y - 4}
                      textAnchor="middle"
                      className={inside ? 'fill-slate-950' : 'fill-slate-200'}
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        pointerEvents: 'none',
                        ...(inside ? { paintOrder: 'stroke', stroke: 'rgba(255,255,255,0.35)', strokeWidth: 2, strokeLinejoin: 'round' } : null),
                      }}
                      opacity={dim ? 0.5 : 1}
                    >
                      {Number(count).toFixed(2)}
                    </text>
                    {/* Dataset label */}
                    <text
                      x={x}
                      y={svgH - 8}
                      textAnchor="middle"
                      className={hover === index ? 'fill-slate-300' : 'fill-slate-500'}
                      style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase' }}
                    >
                      {getDisplayLabel(labels[index])}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Baseline */}
            <line x1={padL} y1={baselineY} x2={svgW - padR} y2={baselineY} stroke="#334155" strokeWidth="1.25" />
          </svg>
        ) : (
          // Empty state: show placeholder text in center of chart
          <div className={`flex items-center justify-center ${fillHeight ? 'h-full' : 'h-48 md:h-56'}`}>
            <div className="text-slate-500 text-sm text-center">
              <div>No data to display</div>
              <div className="text-xs mt-1 text-slate-600">Select players to view vote data</div>
            </div>
          </div>
        )}
      </div>

      {/* Slider Control - Only show if not hidden */}
      {!hideSlider && (
        <div className="space-y-3 mt-3 pt-3 border-t border-slate-800/70">
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Vote Threshold</span>
            <span className="text-emerald-300 text-sm font-mono font-bold tabular-nums">{currentThreshold}</span>
          </div>

          <div className="relative">
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              step="1"
              value={sliderValue}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                // Ensure value always ends in .5
                const newValue = Math.floor(val) + 0.5;
                setSliderValue(newValue);
              }}
              className="vote-threshold-slider w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              list="tickmarks"
            />
          </div>

          <datalist id="tickmarks">
            {(() => {
              const ticks = [];
              for (let i = sliderMin; i <= sliderMax; i += 1) {
                ticks.push(<option key={i} value={i} />);
              }
              return ticks;
            })()}
          </datalist>
        </div>
      )}
    </div>
  );
};

export default VoteBarChart;
